/**
 * An idea — Spec 022, round-3 mockup 5, approved by Alexander.
 *
 * Its header reads like a task's: whose idea, what it is, and the decision
 * right there — «В задачу / Отклонить» for the partner, «Отозвать» for the
 * author (an author never converts their own idea). Below, one block at a time:
 * the discussion first, then the text, similar ideas, files and the history of
 * decisions. Reading never marks the idea as taken for review (`peek`).
 */

import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Ideas, Me } from "@/api/resources";
import type { IdeaOut } from "@/api/types";
import { AttachmentsSection } from "@/components/AttachmentsSection";
import { ChatThread } from "@/components/ChatThread";
import { IdeaDecisions, ReopenIdea, ReturnedBadge } from "@/components/IdeaReturn";
import { ConvertedIdeaBanner, IdeaWorkLinks } from "@/components/IdeaWork";
import { SimilarIdeas } from "@/components/SimilarIdeas";
import { WithdrawIdea } from "@/components/WithdrawIdea";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Mark, Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { AppBar, ago, blockPanel, BlockTabs, EmptyNote, genitive, type BlockTab } from "@/kit";
import { Markdown } from "@/lib/markdown";
import { nameOf, usePeople } from "@/lib/people";
import { useBackButton } from "@/lib/telegram-ui";

type Block = "discussion" | "text" | "similar" | "files" | "history";
const BLOCKS: Block[] = ["discussion", "text", "similar", "files", "history"];

const MARK: Partial<Record<IdeaOut["status"], string>> = {
  converted: "стала задачей",
  approved: "принята",
  rejected: "отклонена",
  withdrawn: "отозвана автором",
};

export function IdeaScreenRoute() {
  useBackButton("/");
  const { idea_id } = useParams<{ idea_id: string }>();
  return idea_id ? <IdeaScreen ideaId={idea_id} /> : null;
}

export function IdeaScreen({ ideaId }: { ideaId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const people = usePeople();
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const idea = useQuery({ queryKey: ["ideas", "one", ideaId], queryFn: () => Ideas.peek(ideaId) });
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const refresh = () => { for (const key of [["ideas"], ["overview"], ["tasks"]]) void qc.invalidateQueries({ queryKey: key }); };
  const approve = useMutation({
    mutationFn: () => Ideas.approve(ideaId, { version: idea.data!.version }),
    onSuccess: (res) => { refresh(); toast.success("Идея стала задачей"); navigate(`/tasks/${res.task.id}`); },
    onError: (e: Error) => toast.error(e.message),
  });
  const reject = useMutation({
    mutationFn: () => Ideas.reject(ideaId, reason.trim()),
    onSuccess: () => { refresh(); setRejecting(false); toast.success("Идея отклонена"); },
    onError: (e: Error) => toast.error(e.message),
  });

  const fromUrl = params.get("block");
  const block: Block = fromUrl && (BLOCKS as string[]).includes(fromUrl) ? (fromUrl as Block) : "discussion";
  const choose = (key: Block) => {
    const next = new URLSearchParams(params);
    next.set("block", key);
    setParams(next, { replace: true });
  };

  if (idea.isLoading) return <SkeletonList count={3} />;
  if (idea.isError || !idea.data) return <EmptyNote>Идея не найдена.</EmptyNote>;

  const i = idea.data;
  const author = me.data?.id === i.author_id;
  const open = i.status === "new" || i.status === "reviewing";
  const authorName = nameOf(people.data, i.author_id) ?? "участника";
  const title = i.summary?.trim() || i.original_text.split("\n", 1)[0].slice(0, 160);
  const hasHistory = (i.decisions?.length ?? 0) > 0 || i.status === "rejected" || i.status === "withdrawn";
  const tabs: BlockTab<Block>[] = [
    { key: "discussion", label: "Обсуждение" },
    { key: "text", label: "Текст идеи" },
    { key: "similar", label: "Похожие" },
    { key: "files", label: "Файлы", count: i.attachments_count || undefined },
    { key: "history", label: "История", hidden: !hasHistory },
  ];

  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/" context="Идея" />
      {i.status === "converted" && <ConvertedIdeaBanner idea={i} />}
      <div className="flex flex-wrap items-center gap-2">
        {open && !author && <Status tone="needs" />}
        {open && author && <Mark>ждёт решения партнёра</Mark>}
        {MARK[i.status] && <Mark>{MARK[i.status]}</Mark>}
        <span className="text-meta text-p-text-muted">{author ? "твоя идея" : `идея ${genitive(authorName)}`} · {ago(i.created_at)}</span>
      </div>
      <h1 className="text-[21px] font-bold leading-[27px] text-p-text lg:text-[26px] lg:leading-8">{title}</h1>

      {open && !author && (rejecting ? (
        <div className="space-y-2">
          <TextArea label="Почему отклоняем — автор увидит" value={reason} rows={3} onChange={(e) => setReason(e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <Button variant="strong" disabled={!reason.trim()} loading={reject.isPending} onClick={() => reject.mutate()}>Отклонить</Button>
            <Button variant="secondary" onClick={() => setRejecting(false)}>Отмена</Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Button variant="strong" loading={approve.isPending} onClick={() => approve.mutate()}>В задачу</Button>
          <Button variant="secondary" onClick={() => setRejecting(true)}>Отклонить</Button>
        </div>
      ))}
      {open && author && <WithdrawIdea idea={i} onDone={refresh} />}

      <BlockTabs label="Блоки идеи" id="idea-blocks" tabs={tabs} value={block} onChange={choose} />
      <div {...blockPanel("idea-blocks", block)} className="flex flex-col gap-3 pt-1">
        {block === "discussion" && (
          <ChatThread key={i.id} context={{ parent_type: "idea", parent_id: i.id }} hideSearch stickyComposer placeholder="Написать в идею" />
        )}
        {block === "text" && (
          <div className="space-y-3 rounded-card border border-p-border bg-p-surface px-4 py-3.5">
            <Markdown className="text-body" text={i.body_md || i.original_text} />
            {i.status !== "converted" && <IdeaWorkLinks ideaId={i.id} />}
          </div>
        )}
        {block === "similar" && (
          <>
            <SimilarIdeas ideaId={i.id} query={i.summary || i.original_text} />
            <p className="text-meta text-p-text-muted">Здесь — похожие идеи, открытые и отклонённые, с причиной отказа. Пусто — значит, похожего нет.</p>
          </>
        )}
        {block === "files" && <AttachmentsSection parentType="idea" parentId={i.id} />}
        {block === "history" && (
          <div className="space-y-3 rounded-card border border-p-border bg-p-surface px-4 py-3.5">
            <ReturnedBadge idea={i} />
            <IdeaDecisions idea={i} />
            <ReopenIdea idea={i} myUserId={me.data?.id} onDone={refresh} />
          </div>
        )}
      </div>
    </div>
  );
}
